import React, { useRef, useState, useEffect } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import PublicPageLayout from '../../shared/components/PublicPageLayout';

// Program Card Component
const ProgramCard = ({ iconUrl, name, description, minAge, category, t }) => (
  <div className="col-md-6 col-lg-4 mb-4">
    <div
      className="card h-100 border-0"
      style={{
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <div className="card-body p-4">
        <div className="d-flex align-items-start mb-3">
          {/* Program Icon */}
          <div
            className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: '#f3f4f6',
            }}
          >
            <img
              src={iconUrl}
              alt={name}
              style={{ width: '36px', height: '36px', objectFit: 'contain' }}
            />
          </div>

          {/* Program Info */}
          <div className="flex-grow-1">
            <h4 className="fw-bold mb-1" style={{ fontSize: '18px', color: '#111827' }}>
              {name}
            </h4>
            <div className="d-flex flex-wrap gap-2">
              {/* Age Badge */}
              <span
                className="badge"
                style={{
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {t('programs.ages', { age: minAge })}
              </span>
              {/* Category Badge */}
              <span
                className="badge"
                style={{
                  backgroundColor: '#f3e8ff',
                  color: '#7c3aed',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {category}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p
          className="mb-0"
          style={{ fontSize: '14px', color: '#4b5563', lineHeight: '22px' }}
        >
          {description}
        </p>
      </div>
    </div>
  </div>
);

// CTA Section
const CTASection = ({ loginPath, t }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(16.88deg, #667eea 50%, #764ba2 120.71%)',
    }}
  >
    <div className="container py-5">
      <div className="text-center text-white">
        <h2 className="fw-bold mb-4" style={{ fontSize: '42px', lineHeight: '50px' }}>
          {t('programs.cta.title')}
        </h2>
        <p
          className="mb-4 mx-auto"
          style={{ fontSize: '20px', maxWidth: '700px', color: '#f3f4f6', lineHeight: '30px' }}
        >
          {t('programs.cta.description')}
        </p>
        <a
          href={loginPath}
          className="btn btn-light btn-lg fw-semibold px-5"
          style={{ borderRadius: '8px', color: '#667eea' }}
        >
          {t('programs.cta.button')}
        </a>
      </div>
    </div>
  </section>
);

// Main Programs Page Component
const ProgramsPage = ({ locale = 'vi', loginPath = '/login', flashNotice, flashAlert }) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();

  // State for programs from API
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch programs from API
  useEffect(() => {
    fetch('/api/public/programs')
      .then((res) => res.json())
      .then((data) => {
        if (data.programs) {
          setPrograms(data.programs);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  return (
    <PublicPageLayout
      loginPath={loginPath}
      flashNotice={flashNotice}
      flashAlert={flashAlert}
      t={t}
    >
      {/* Hero Section */}
      <section
        className="py-5"
        style={{
          background: 'linear-gradient(142.52deg, #faf5ff 0%, #eff6ff 70.71%)',
        }}
      >
        <div className="container py-4">
          <div className="text-center mb-5">
            <h1
              className="fw-bold mb-3"
              style={{ fontSize: '48px', color: '#111827', lineHeight: '56px' }}
            >
              {t('programs.title')}
            </h1>
            <p
              className="mx-auto"
              style={{ fontSize: '20px', color: '#4b5563', maxWidth: '700px' }}
            >
              {t('programs.subtitle')}
            </p>
          </div>

          {/* Programs Grid */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
          ) : (
            <div className="row">
              {programs.map((program) => (
                <ProgramCard
                  key={program.key}
                  iconUrl={program.icon_url}
                  name={program.name}
                  description={program.description}
                  minAge={program.min_age}
                  category={program.category}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <CTASection loginPath={loginPath} t={t} />
    </PublicPageLayout>
  );
};

export default ProgramsPage;
